import ArrayUtils from '../../utils/ArrayUtils'
import MercatorProjection from './MercatorProjection'
import Point from '../../geom/Point'
import Location from '../Location'
import BaseWithReferenceAreaProjection from './BaseWithReferenceAreaProjection'

function MercatorPlusProjection(referenceAreas, referencePoints, rotation, anchor, offset) {
    BaseWithReferenceAreaProjection.call(this, referenceAreas, referencePoints);

    this._rotation = (typeof rotation === 'undefined') ? 0 : rotation;
    this._anchor = (typeof anchor === 'undefined') ? null : anchor;
    this._offset = (typeof offset === 'undefined') ? null : offset;
}
MercatorPlusProjection.prototype = new BaseWithReferenceAreaProjection();

MercatorPlusProjection.prototype.locationToPoint = function(location) {
    var mp = this._createProjectionForLocation(location);
    var result = mp.locationToPoint(location);
    if (this._anchor != null && this._rotation != 0) {
        result.rotate(this._rotation, this._anchor);
        if (this._offset) {
            result.offset(-this._offset.x, -this._offset.y);
        }
    }
    return result;
};

MercatorPlusProjection.prototype.pointToLocation = function(point) {
    var mapPoint = point.clone();
    if (this._anchor != null && this._rotation != 0) {
        if (this._offset) {
            mapPoint.offset(this._offset.x, this._offset.y);
        }
        mapPoint.rotate(-this._rotation, this._anchor);
    }
    var mp = this._createProjectionForPoint(mapPoint);
    return mp.pointToLocation(point);
};

MercatorPlusProjection.prototype._createProjection = function(topRefPoint, leftRefPoint, bottomRefPoint, rightRefPoint) {
    var mp = new MercatorProjection(new Point(leftRefPoint.point.x, topRefPoint.point.y),
        new Location(topRefPoint.location.latitude, leftRefPoint.location.longitude),
        new Point(rightRefPoint.point.x, bottomRefPoint.point.y),
        new Location(bottomRefPoint.location.latitude, rightRefPoint.location.longitude));
    return mp;
};

MercatorPlusProjection.prototype._createProjectionForPoint = function(point) {
    function compareRefPoint(refPoint1, refPoint2) {
        var refPoint1DeltaX = refPoint1.point.x - point.x;
        var refPoint1DeltaY = refPoint1.point.y - point.y;
        var refPoint1Distance = Math.sqrt(Math.pow(refPoint1DeltaX, 2) + Math.pow(refPoint1DeltaY, 2));

        var refPoint2DeltaX = refPoint2.point.x - point.x;
        var refPoint2DeltaY = refPoint2.point.y - point.y;
        var refPoint2Distance = Math.sqrt(Math.pow(refPoint2DeltaX, 2) + Math.pow(refPoint2DeltaY, 2));

        if (refPoint1Distance < refPoint2Distance) {
            return -1;
        } else if (refPoint1Distance > refPoint2Distance) {
            return 1;
        }

        return 0;
    }

    var references = this._getAreaReferences(this.isPointInArea, point);
    var sortedReferences = ArrayUtils.copy(references);
    sortedReferences.sort(compareRefPoint);
    var topRefPoint = null;
    var leftRefPoint = null;
    var bottomRefPoint = null;
    var rightRefPoint = null;

    for (var i = 0; i < sortedReferences.length; i++) {
        var refPoint = sortedReferences[i];
        if (topRefPoint == null && (refPoint.point.y <= point.y)) {
            topRefPoint = refPoint;
        } else if (bottomRefPoint == null && (refPoint.point.y >= point.y)) {
            bottomRefPoint = refPoint;
        }

        if (leftRefPoint == null && (refPoint.point.x <= point.x)) {
            leftRefPoint = refPoint;
        } else if (rightRefPoint == null && (refPoint.point.x >= point.x)) {
            rightRefPoint = refPoint;
        }

        if (topRefPoint != null && leftRefPoint != null && bottomRefPoint != null && rightRefPoint != null) {
            break;
        }
    }
    if (topRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != bottomRefPoint) {
                topRefPoint = refPoint;
                break;
            }
        }
    }

    if (leftRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != rightRefPoint) {
                leftRefPoint = refPoint;
                break;
            }
        }
    }

    if (bottomRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != topRefPoint) {
                bottomRefPoint = refPoint;
                break;
            }
        }
    }

    if (rightRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != leftRefPoint) {
                rightRefPoint = refPoint;
                break;
            }
        }
    }
    return this._createProjection(topRefPoint, leftRefPoint, bottomRefPoint, rightRefPoint);
};

MercatorPlusProjection.prototype._createProjectionForLocation = function(location) {
    function compareRefPoint(refPoint1, refPoint2) {
        if (location != null) {
            var refPoint1DeltaX = refPoint1.location.longitude - location.longitude;
            var refPoint1DeltaY = refPoint1.location.latitude - location.latitude;
            var refPoint1Distance = Math.sqrt(Math.pow(refPoint1DeltaX, 2) + Math.pow(refPoint1DeltaY, 2));
            var refPoint2DeltaX = refPoint2.location.longitude - location.longitude;
            var refPoint2DeltaY = refPoint2.location.latitude - location.latitude;
            var refPoint2Distance = Math.sqrt(Math.pow(refPoint2DeltaX, 2) + Math.pow(refPoint2DeltaY, 2));

            if (refPoint1Distance < refPoint2Distance) {
                return -1;
            } else if (refPoint1Distance > refPoint2Distance) {
                return 1;
            }
        }
        return 0;
    }
    var references = this._getAreaReferences(this.isLocationInArea, location);
    var sortedReferences = ArrayUtils.copy(references);
    sortedReferences.sort(compareRefPoint);
    var topRefPoint = null;
    var leftRefPoint = null;
    var bottomRefPoint = null;
    var rightRefPoint = null;

    for (var i = 0; i < sortedReferences.length; i++) {
        var refPoint = sortedReferences[i];
        if (topRefPoint == null && (refPoint.location.latitude >= location.latitude)) {
            topRefPoint = refPoint;
        } else if (bottomRefPoint == null && (refPoint.location.latitude <= location.latitude)) {
            bottomRefPoint = refPoint;
        }

        if (leftRefPoint == null && (refPoint.location.longitude <= location.longitude)) {
            leftRefPoint = refPoint;
        } else if (rightRefPoint == null && (refPoint.location.longitude >= location.longitude)) {
            rightRefPoint = refPoint;
        }

        if (topRefPoint != null && leftRefPoint != null && bottomRefPoint != null && rightRefPoint != null) {
            break;
        }
    }

    if (topRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != bottomRefPoint) {
                topRefPoint = refPoint;
                break;
            }
        }
    }

    if (leftRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != rightRefPoint) {
                leftRefPoint = refPoint;
                break;
            }
        }
    }

    if (bottomRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != topRefPoint) {
                bottomRefPoint = refPoint;
                break;
            }
        }
    }

    if (rightRefPoint == null) {
        // eslint-disable-next-line no-redeclare
        for (var i = 0; i < sortedReferences.length; i++) {
            // eslint-disable-next-line no-redeclare
            var refPoint = sortedReferences[i];
            if (refPoint != leftRefPoint) {
                rightRefPoint = refPoint;
                break;
            }
        }
    }
    return this._createProjection(topRefPoint, leftRefPoint, bottomRefPoint, rightRefPoint);

};

export default MercatorPlusProjection;